/**
 * Turning the customer-linking model into the words the page shows.
 *
 * Headless on purpose: these are the sentences a non-technical reader has to
 * act on, and they are the same sentences whatever shell renders them. The
 * vocabulary is fixed — "link" is a decision somebody made, "match" is the
 * system's suggestion, and no label ever uses one to mean the other.
 */

import type {
  PromoOfficeCustomerLink,
  PromoOfficeMatchedSignal,
  PromoOfficeSupplierLink,
  PromoOfficeSupplierMatchedSignal,
  PromoOfficeUnlinkedCustomer,
  PromoOfficeUnlinkedSupplier,
} from "../data/adapter";

/**
 * What agreed, in words.
 *
 * A person cannot judge "is this the same company?" from a rank, only from the
 * evidence — so this never abbreviates to a score or a percentage. Both
 * signals agreeing is the strong case; name alone is the one that needs a
 * human to look at the postcodes either side of it.
 */
export function describeMatchedSignals(
  signals: PromoOfficeMatchedSignal[],
): string {
  const hasName = signals.includes("name");
  const hasPostcode = signals.includes("postcode");
  if (hasName && hasPostcode) return "Name and postcode match";
  if (hasName) return "Name matches, postcode does not";
  if (hasPostcode) return "Postcode matches, name does not";
  // Not reachable from the backend, which only ever records a match on at
  // least one signal — but a suggestion with no stated reason must never
  // render as an empty cell that looks like a loading state.
  return "No stated reason";
}

/**
 * How a link came to exist. Provenance, not confidence.
 *
 * "Matched automatically" rather than "auto-linked": the reader needs to know
 * nobody looked at it, because that is the one kind of link most worth
 * double-checking when something downstream looks wrong.
 */
export function describeLinkDecision(
  decidedBy: PromoOfficeCustomerLink["decidedBy"],
): string {
  switch (decidedBy) {
    case "confident_auto":
      return "Matched automatically";
    case "human_confirmed":
      return "Linked by hand";
    case "human_created":
      return "Customer in One created for it";
  }
}

/**
 * "3 quotes and 1 order", or whichever half is non-zero.
 *
 * This is what makes the list worth ordering by impact, so it has to read as a
 * quantity rather than a badge. A customer with neither cannot appear in the
 * list at all, but the empty string is handled rather than asserted — a row
 * that renders "and" with nothing either side is worse than one that renders
 * nothing.
 */
export function describeOpenWork(
  customer: Pick<
    PromoOfficeUnlinkedCustomer,
    "openQuoteCount" | "openOrderCount"
  >,
): string {
  const parts: string[] = [];
  if (customer.openQuoteCount > 0) {
    parts.push(
      `${customer.openQuoteCount} ${customer.openQuoteCount === 1 ? "quote" : "quotes"}`,
    );
  }
  if (customer.openOrderCount > 0) {
    parts.push(
      `${customer.openOrderCount} ${customer.openOrderCount === 1 ? "order" : "orders"}`,
    );
  }
  return parts.join(" and ");
}

/**
 * Whether a link routes to anybody.
 *
 * A linked customer with no sales rep and no CSR is a real state, not a bug:
 * it sits in no portfolio, or in the ownerless House Account. The page says so
 * rather than showing a blank, because "linked" otherwise implies somebody has
 * been told and nobody has.
 */
export function hasSomebodyToTell(link: PromoOfficeCustomerLink): boolean {
  return link.salesRepUserId !== null || link.csrUserId !== null;
}

// ----- Supplier linking --------------------------------------------------------
// The same sentences for the supplier twin. A supplier has a third signal the
// customer does not: another Promo Office spelling of the name, already
// linked, carries the same creditor number.

/**
 * What agreed, in words, for a supplier suggestion.
 *
 * Says what did *not* agree as well as what did, because a single-signal
 * suggestion's whole job for the reader is to check the thing that disagreed.
 */
export function describeSupplierMatchedSignals(
  signals: PromoOfficeSupplierMatchedSignal[],
): string {
  const agreed: string[] = [];
  const disagreed: string[] = [];
  (["name", "creditor", "postcode"] as const).forEach((signal) => {
    (signals.includes(signal) ? agreed : disagreed).push(
      signal === "creditor" ? "creditor number" : signal,
    );
  });
  if (agreed.length === 0) return "No stated reason";
  const agreedWords = joinWithAnd(agreed);
  const verb = agreed.length === 1 ? "matches" : "match";
  const sentence = `${capitalise(agreedWords)} ${verb}`;
  if (disagreed.length === 0) return sentence;
  return `${sentence}, ${joinWithAnd(disagreed)} ${disagreed.length === 1 ? "does" : "do"} not`;
}

function joinWithAnd(words: string[]): string {
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

function capitalise(sentence: string): string {
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

/**
 * How a supplier link came to exist. Both are a person's decision: the system
 * never links a supplier itself, because a wrong link raises a real purchase
 * order to the wrong supplier.
 */
export function describeSupplierLinkDecision(
  decidedBy: PromoOfficeSupplierLink["decidedBy"],
): string {
  switch (decidedBy) {
    case "human_confirmed":
      return "Suggestion confirmed";
    case "human_chosen":
      return "Chosen by hand";
  }
}

/**
 * "4 quote lines, 2 order lines and 1 purchase order", or whichever parts are
 * non-zero. The list is worked by impact, so this reads as a quantity.
 */
export function describeSupplierOpenWork(
  supplier: Pick<
    PromoOfficeUnlinkedSupplier,
    "openTenderLines" | "openIndentLines" | "openPurchaseOrders"
  >,
): string {
  const parts: string[] = [];
  if (supplier.openTenderLines > 0) {
    parts.push(
      `${supplier.openTenderLines} quote ${supplier.openTenderLines === 1 ? "line" : "lines"}`,
    );
  }
  if (supplier.openIndentLines > 0) {
    parts.push(
      `${supplier.openIndentLines} order ${supplier.openIndentLines === 1 ? "line" : "lines"}`,
    );
  }
  if (supplier.openPurchaseOrders > 0) {
    parts.push(
      `${supplier.openPurchaseOrders} purchase ${supplier.openPurchaseOrders === 1 ? "order" : "orders"}`,
    );
  }
  return joinWithAnd(parts);
}
